import {
  useCallback,
  useImperativeHandle,
  forwardRef,
  useRef,
  useState,
} from "react";
import { PIPELINE_CARD_WIDTH_RATIO } from "../constants/pipelineLayout";

export const FALL_DURATION_MS = 1100;
export const ARC_DURATION_MS = 900;
export const MIN_GAP_SKY_MS = 550;
export const MIN_GAP_ARC_MS = 400;

function easeOutStrong(t) {
  return 1 - Math.pow(1 - t, 2.2);
}

function easeInOutSmooth(t) {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

function arcHeightFromDx(dx) {
  return Math.min(180, 60 + Math.abs(dx) * 0.18);
}

function hexToRgba(hex, alpha) {
  if (!hex || typeof hex !== "string") return `rgba(234,179,8,${alpha})`;
  let h = hex.replace("#", "");
  if (h.length === 3) h = h.split("").map((ch) => ch + ch).join("");
  const n = parseInt(h, 16);
  if (Number.isNaN(n)) return `rgba(234,179,8,${alpha})`;
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return `rgba(${r},${g},${b},${alpha})`;
}

function waitDoubleRaf() {
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(resolve);
    });
  });
}

function runRafAnimation(frameFn) {
  return new Promise((resolve) => {
    let rafId;
    const tick = () => {
      rafId = requestAnimationFrame(() => {
        if (frameFn()) {
          cancelAnimationFrame(rafId);
          resolve();
        } else tick();
      });
    };
    tick();
  });
}

const TrailSvg = forwardRef(function TrailSvg(_, ref) {
  return (
    <svg
      ref={ref}
      aria-hidden
      style={{
        position: "fixed",
        inset: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
        zIndex: 1,
      }}
    />
  );
});

export const PipelineLeadMotionOverlay = forwardRef(function PipelineLeadMotionOverlay(
  {
    theme,
    etapasLeads,
    speedMultiplier,
    LeadCard,
    colHeaderRefs,
    colBodyRefs,
    leadCardRefs,
    onReceivingColumn,
  },
  ref
) {
  const isDark = theme.name === "dark";
  const trailSvgRef = useRef(null);
  const trailBufRef = useRef([]);

  const [ghost, setGhost] = useState(null);

  const lastSkyAtRef = useRef(0);
  const lastArcAtRef = useRef(0);
  const chainRef = useRef(Promise.resolve());

  const speed = Math.max(0.2, Math.min(3, speedMultiplier || 0.8));

  const etapaOf = useCallback(
    (key) => etapasLeads.find((e) => e.key === key) || etapasLeads[0],
    [etapasLeads]
  );

  const flushTrail = useCallback((color) => {
    const svg = trailSvgRef.current;
    const samples = trailBufRef.current;
    if (!svg) return;
    while (svg.firstChild) svg.removeChild(svg.firstChild);
    const W = window.innerWidth;
    const H = window.innerHeight;
    svg.setAttribute("viewBox", `0 0 ${W} ${H}`);
    for (const s of samples) {
      const circle = document.createElementNS("http://www.w3.org/2000/svg", "circle");
      circle.setAttribute("cx", String(s.x));
      circle.setAttribute("cy", String(s.y));
      circle.setAttribute("r", String(s.r));
      circle.setAttribute("fill", hexToRgba(color, s.a));
      svg.appendChild(circle);
    }
  }, []);

  const trailPush = useCallback(
    (x, y, color, fade = 1) => {
      const now = performance.now();
      const buf = trailBufRef.current;
      buf.push({ x, y, r: 2.4, a: 0.44 * fade, born: now });
      trailBufRef.current = buf.filter((s) => now - s.born < 440);
      flushTrail(color);
    },
    [flushTrail]
  );

  const trailClear = useCallback(() => {
    trailBufRef.current = [];
    const svg = trailSvgRef.current;
    if (svg) while (svg.firstChild) svg.removeChild(svg.firstChild);
  }, []);

  const waitThrottle = async (lastRef, minMs) => {
    const now = performance.now();
    const elapsed = now - lastRef.current;
    const need = minMs / speed - elapsed;
    if (need > 0) await new Promise((r) => setTimeout(r, need));
    lastRef.current = performance.now();
  };

  const runFall = useCallback(
    async ({ item, onLand }) => {
      const leadsBody = colBodyRefs.current?.leads;
      const leadsHeader = colHeaderRefs.current?.leads;
      if (!leadsBody) {
        await Promise.resolve(onLand?.());
        return;
      }
      const bodyRect = leadsBody.getBoundingClientRect();
      const cardW = Math.min(
        320,
        Math.max(200, bodyRect.width * PIPELINE_CARD_WIDTH_RATIO - 8)
      );
      const destX = bodyRect.left + (bodyRect.width - cardW) / 2;
      const destY = bodyRect.top + 8;
      let hCenter = bodyRect.left + bodyRect.width / 2;
      if (leadsHeader) {
        const hr = leadsHeader.getBoundingClientRect();
        hCenter = hr.left + hr.width / 2;
      }
      const jitterX = (Math.random() - 0.5) * 80;
      const startX = hCenter - cardW / 2 + jitterX;
      const startY = -80;
      const rotSign = jitterX >= 0 ? 1 : -1;
      const rot0 = rotSign * 6;
      const dur = FALL_DURATION_MS / speed;
      const swayAmp = 10 + Math.random() * 6;
      const leadsColor = etapaOf("leads").color;
      let frame = 0;

      const t0 = performance.now();

      await runRafAnimation(() => {
        const elapsed = performance.now() - t0;
        const t = Math.min(1, elapsed / dur);
        const te = easeOutStrong(t);
        const sway = Math.sin(elapsed / (dur * 0.32)) * swayAmp * (1 - te * 0.12);
        const x = startX + (destX - startX) * te + sway;
        const y = startY + (destY - startY) * te;
        const sc = 0.7 + 0.3 * te;
        const rot = rot0 * (1 - te);
        const shY = 4 + te * 12;
        const shBlur = 12 + te * 22;
        setGhost({
          type: "fall",
          item,
          etapa: etapaOf("leads"),
          x,
          y,
          w: cardW,
          scale: sc,
          rotate: rot,
          shadow: `0 ${shY}px ${shBlur}px rgba(0,0,0,${isDark ? 0.5 : 0.14})`,
          flash: t >= 1 ? leadsColor : null,
          borderPulse: t >= 1,
        });
        frame++;
        if (frame % 2 === 0) trailPush(x + cardW / 2, y + 28, leadsColor, 1);
        if (t >= 1) return true;
        return false;
      });

      setGhost(null);
      trailClear();
      await Promise.resolve(onLand?.());
    },
    [colBodyRefs, colHeaderRefs, etapaOf, isDark, trailClear, trailPush, speed]
  );

  const runArc = useCallback(
    async ({ item, fromKey, toKey, onBeforeStart, onMidFlight, onLand }) => {
      const hFrom = colHeaderRefs.current?.[fromKey];
      const hTo = colHeaderRefs.current?.[toKey];
      const bodyFrom = colBodyRefs.current?.[fromKey];
      const bodyTo = colBodyRefs.current?.[toKey];
      if (!hFrom || !hTo || !bodyFrom || !bodyTo) {
        onBeforeStart?.();
        await Promise.resolve(onMidFlight?.());
        await Promise.resolve(onLand?.());
        onReceivingColumn?.(null);
        return;
      }
      await waitDoubleRaf();
      const rToBody = bodyTo.getBoundingClientRect();
      const bodyFromRect = bodyFrom.getBoundingClientRect();

      const cardW = Math.min(
        320,
        Math.max(
          200,
          Math.min(bodyFromRect.width, rToBody.width) * PIPELINE_CARD_WIDTH_RATIO - 8
        )
      );

      const cardEl = leadCardRefs.current?.[item.id];
      const rCard = cardEl?.getBoundingClientRect();
      let cardH = rCard && rCard.height > 40 ? rCard.height : 120;

      const slotX = (bodyRect) => bodyRect.left + (bodyRect.width - cardW) / 2;
      const slotY = (bodyRect) => bodyRect.top + 8;

      let x0 = slotX(bodyFromRect);
      let y0 = slotY(bodyFromRect);
      if (rCard && rCard.width > 40) {
        const cx = rCard.left + rCard.width / 2;
        const cy = rCard.top + rCard.height / 2;
        if (
          cx >= bodyFromRect.left &&
          cx <= bodyFromRect.right &&
          cy >= bodyFromRect.top &&
          cy <= bodyFromRect.bottom
        ) {
          x0 = rCard.left;
          y0 = rCard.top;
          cardH = rCard.height;
        }
      }

      const destX = slotX(rToBody);
      const destY = slotY(rToBody);
      const dx = destX - x0;
      const peakH = arcHeightFromDx(dx);
      const midX = (x0 + destX) / 2;
      const midY = Math.min(y0, destY) - peakH;
      const dur = ARC_DURATION_MS / speed;
      const destMeta = etapaOf(toKey);
      const destColor = destMeta.color;
      const rotTo = dx >= 0 ? 8 : -8;
      let frame = 0;
      let midSent = false;

      onBeforeStart?.();
      onReceivingColumn?.(toKey);
      const t0 = performance.now();

      await runRafAnimation(() => {
        const elapsed = performance.now() - t0;
        const u = Math.min(1, elapsed / dur);
        const t = easeInOutSmooth(u);
        const omt = 1 - t;
        const x = omt * omt * x0 + 2 * omt * t * midX + t * t * destX;
        const y = omt * omt * y0 + 2 * omt * t * midY + t * t * destY;
        const peakFactor = Math.sin(Math.PI * u);
        const sc = 1 + 0.06 * peakFactor;
        const rot = rotTo * peakFactor;
        const shSpread = 8 + peakFactor * 18;
        const shAlpha = isDark ? 0.35 + peakFactor * 0.25 : 0.08 + peakFactor * 0.14;
        setGhost({
          type: "arc",
          item,
          etapa: destMeta,
          x,
          y,
          w: cardW,
          h: cardH,
          scale: sc,
          rotate: rot,
          shadow: `0 ${shSpread}px ${24 + peakFactor * 28}px rgba(0,0,0,${shAlpha})`,
          flash: u >= 1 ? destColor : null,
          borderPulse: u >= 1,
        });
        frame++;
        if (frame % 2 === 0) trailPush(x + cardW / 2, y + cardH * 0.45, destColor, 1 - u * 0.35);
        if (!midSent && u >= 0.5) {
          midSent = true;
          onMidFlight?.();
        }
        if (u >= 1) return true;
        return false;
      });

      setGhost(null);
      trailClear();
      await Promise.resolve(onLand?.());
      setTimeout(() => onReceivingColumn?.(null), 120);
    },
    [colBodyRefs, colHeaderRefs, etapaOf, isDark, leadCardRefs, onReceivingColumn, trailClear, trailPush, speed]
  );

  useImperativeHandle(
    ref,
    () => ({
      enqueueSkyFall(payload) {
        const run = () => waitThrottle(lastSkyAtRef, MIN_GAP_SKY_MS).then(() => runFall(payload));
        const next = chainRef.current.then(run);
        chainRef.current = next.catch(() => {});
        return next;
      },
      enqueueArc(payload) {
        const run = () => waitThrottle(lastArcAtRef, MIN_GAP_ARC_MS).then(() => runArc(payload));
        const next = chainRef.current.then(run);
        chainRef.current = next.catch(() => {});
        return next;
      },
    }),
    [runArc, runFall]
  );

  return (
    <div
      aria-hidden
      style={{
        position: "fixed",
        inset: 0,
        pointerEvents: "none",
        zIndex: 42,
        overflow: "hidden",
      }}
    >
      <TrailSvg ref={trailSvgRef} />
      {ghost && LeadCard && (
        <div
          style={{
            position: "fixed",
            left: ghost.x,
            top: ghost.y,
            width: ghost.w,
            minHeight: ghost.h || undefined,
            transform: `scale(${ghost.scale}) rotate(${ghost.rotate}deg)`,
            transformOrigin: "center top",
            boxShadow: ghost.shadow,
            zIndex: 100,
            borderRadius: 10,
            outline: ghost.borderPulse ? `2px solid ${ghost.flash || ghost.etapa.color}` : "none",
            outlineOffset: ghost.borderPulse ? 2 : 0,
            background: ghost.flash ? hexToRgba(ghost.flash, 0.25) : "transparent",
          }}
        >
          <LeadCard
            item={ghost.item}
            etapa={ghost.etapa}
            theme={theme}
            onClick={() => {}}
            onDragStart={() => {}}
            motionGhost
          />
        </div>
      )}
    </div>
  );
});
